import * as React from "react";
import axios from "axios";
import { MyCard } from "./MyCard";
import { DataTable, shortHash } from "./ui";

function fmt(n?: number, d = 2) {
  if (n == null || Number.isNaN(n)) return "—";
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: d });
}

function fmtHashrate(n?: number) {
  if (!n && n !== 0) return "—";
  if (n >= 1e12) return (n / 1e12).toFixed(2) + " TH/s";
  if (n >= 1e9) return (n / 1e9).toFixed(2) + " GH/s";
  if (n >= 1e6) return (n / 1e6).toFixed(2) + " MH/s";
  if (n >= 1e3) return (n / 1e3).toFixed(2) + " kH/s";
  return n.toFixed(0) + " H/s";
}

function addrLink(a: string) {
  return (
    <a className="mono" href={"index.html?route=ADDRESS&address=" + a}>
      {a}
    </a>
  );
}

function fmtMove(n?: number) {
  if (n == null || Number.isNaN(n)) return "—";
  return Number(n).toFixed(8);
}

function fmtUtc(ts?: number) {
  if (!ts) return "—";
  return new Date(ts * 1000).toISOString().replace("T", " ").slice(0, 19);
}

function blockLink(height?: number) {
  if (height == null) return "—";
  return (
    <a href={"index.html?route=BLOCK&hash=" + height}>{height}</a>
  );
}

function partyLink(address?: string) {
  if (!address) return "—";
  if (address === "Coinbase") return <span>Coinbase</span>;
  return addrLink(address);
}

function amountLink(amount: number, txid?: string, signed = true) {
  const negative = amount < 0;
  const text = (signed && negative ? "-" : "") + fmtMove(Math.abs(amount));
  const node = (
    <span className={negative ? "amt-out" : "amt-in"}>{text}</span>
  );
  if (!txid) return node;
  return (
    <a className={negative ? "amt-out" : "amt-in"} href={"index.html?route=TRANSACTION&id=" + txid}>
      {text}
    </a>
  );
}

function Page({
  kicker,
  title,
  lead,
  children,
}: {
  kicker: string;
  title: string;
  lead?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="page stack">
      <div className="kicker">{kicker}</div>
      <h1 className="page-title">{title}</h1>
      {lead ? <p className="muted">{lead}</p> : null}
      {children}
    </div>
  );
}

function useApi<T>(url: string) {
  const [data, setData] = React.useState<T | null>(null);
  const [error, setError] = React.useState("");
  React.useEffect(() => {
    axios
      .get(url)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.message || "Failed to load"));
  }, [url]);
  return { data, error };
}

function Loading({ error }: { error?: string }) {
  if (error) return <div className="banner">{error}</div>;
  return <p className="muted">Loading…</p>;
}

export function BlocksPage() {
  const [before, setBefore] = React.useState<number | undefined>(undefined);
  const url =
    "/api/insights/blocks?limit=25" + (before != null ? "&before=" + before : "");
  const { data, error } = useApi<any>(url);

  if (!data) return <Page kicker="Chain" title="Blocks"><Loading error={error} /></Page>;

  return (
    <Page kicker="Chain" title="Blocks" lead="Newest first. Height, time, transaction count, and hash.">
      <div className="card">
        <DataTable
          headers={["Height", "Time", "Txs", "Size", "Hash"]}
          rows={(data.blocks || []).map((block: any) => {
            const href = "index.html?route=BLOCK&hash=" + block.hash;
            return [
              <a href={href}>{fmt(block.height, 0)}</a>,
              new Date(block.time * 1000).toLocaleString(),
              block.tx,
              block.size ? fmt(block.size, 0) + " B" : "—",
              <a className="mono" href={href}>{shortHash(block.hash)}</a>,
            ];
          })}
        />
      </div>
      <div className="pager">
        <a className="btn" href="index.html?route=BLOCKS">Latest</a>
        {data.next != null ? (
          <button className="btn" onClick={() => setBefore(data.next)}>
            Older
          </button>
        ) : null}
      </div>
    </Page>
  );
}

export function DistributionPage() {
  const { data, error } = useApi<any>("/api/insights/distribution");
  if (!data) return <Page kicker="Supply" title="Coin Distribution"><Loading error={error} /></Page>;
  return (
    <Page
      kicker="Supply"
      title="Coin Distribution"
      lead={"Circulating supply " + fmt(data.supply, 0) + " TLS. Share held by the largest known addresses."}
    >
      <div className="dist-bars">
        {data.buckets.map((b: any) => (
          <div key={b.label} className="dist-row">
            <div className="dist-label">{b.label}</div>
            <div className="dist-track">
              <div className="dist-fill" style={{ width: Math.min(100, b.share) + "%" }} />
            </div>
            <div className="dist-meta">
              {fmt(b.share, 1)}% · {fmt(b.amount, 0)} TLS
            </div>
          </div>
        ))}
      </div>
    </Page>
  );
}

export function RichListPage() {
  const { data, error } = useApi<any>("/api/insights/richlist?limit=50");
  if (!data) return <Page kicker="Addresses" title="Top Addresses"><Loading error={error} /></Page>;
  return (
    <Page
      kicker="Addresses"
      title="Top Addresses"
      lead="Balances read from this node. Ranked from a set of known large holders, refreshed on this explorer."
    >
      <div className="card">
        <DataTable
          headers={["#", "Address", "Balance", "Share"]}
          rows={(data.items || []).map((row: any) => [
            row.rank,
            addrLink(row.address),
            fmt(row.balance, 2) + " TLS",
            fmt(row.share, 2) + "%",
          ])}
        />
      </div>
    </Page>
  );
}

export function MovementsPage() {
  const [data, setData] = React.useState<any>(null);
  const [error, setError] = React.useState("");
  const [hideRewards, setHideRewards] = React.useState(false);
  const [page, setPage] = React.useState(0);

  React.useEffect(() => {
    let n = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const load = () => {
      axios
        .get("/api/insights/movements")
        .then((r) => {
          setData(r.data);
          const empty = !(r.data.list || []).length && !(r.data.topReceived || []).length;
          if ((r.data.scanning || empty) && n < 20) {
            n += 1;
            timer = setTimeout(load, 4000);
          }
        })
        .catch((e) => setError(e.message || "Failed to load"));
    };
    load();
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, []);

  if (!data) return <Page kicker="Flow" title="Coin Movements"><Loading error={error} /></Page>;

  const period = data.scanning && !(data.list || []).length
    ? "Scanning recent blocks…"
    : data.fromHeight
      ? "Last " + data.hours + " hours · blocks " + fmt(data.fromHeight, 0) + "–" + fmt(data.toHeight, 0)
      : "Recent blocks";

  const sentRows = (data.topSent || []).map((m: any) => [
    partyLink(m.address),
    blockLink(m.height),
    amountLink(-Math.abs(m.amount), m.txid),
  ]);
  const recvRows = (data.topReceived || []).map((m: any) => [
    partyLink(m.address),
    blockLink(m.height),
    amountLink(Math.abs(m.amount), m.txid),
  ]);

  const list = (data.list || []).filter((m: any) => !hideRewards || !m.reward);
  const pageSize = 20;
  const pages = Math.max(1, Math.ceil(list.length / pageSize));
  const pageSafe = Math.min(page, pages - 1);
  const slice = list.slice(pageSafe * pageSize, pageSafe * pageSize + pageSize);
  const listRows = slice.map((m: any) => [
    fmtUtc(m.time),
    partyLink(m.address),
    m.txid ? (
      <a className="mono" href={"index.html?route=TRANSACTION&id=" + m.txid}>
        {shortHash(m.txid, 12)}
      </a>
    ) : "—",
    fmt(m.confirmations, 0),
    amountLink(m.amount, m.txid),
  ]);

  return (
    <Page kicker="Flow" title="Coin Movements">
      <div className="move-layout">
        <div className="move-grid">
          <MyCard
            header="Query setup"
            body={
              <div className="query-panel">
                <div>
                  <div className="query-label">Period</div>
                  <div className="query-value">{period}</div>
                </div>
                <label className="query-check">
                  <input
                    type="checkbox"
                    checked={hideRewards}
                    onChange={(e) => {
                      setHideRewards(e.target.checked);
                      setPage(0);
                    }}
                  />
                  Hide block rewards
                </label>
                <p className="muted query-note">
                  Tops are transfers of 1,000 TLS or more. The list is the most recent movements in this window.
                </p>
              </div>
            }
          />
          <MyCard
            header="Coin circulation"
            body={
              <div className="circ-panel">
                <div className="circ-figure">{fmt(data.circulation, 2)}</div>
                <div className="circ-unit">TLS</div>
                <p className="muted query-note">Live UTXO set on this node.</p>
              </div>
            }
          />
        </div>
        <div className="move-grid">
          <MyCard
            header="Top spent"
            body={
              sentRows.length ? (
                <DataTable headers={["Address", "Block height", "Amount (TLS)"]} rows={sentRows} />
              ) : (
                <p className="muted">No spends of 1,000 TLS or more in this window yet.</p>
              )
            }
          />
          <MyCard
            header="Top received"
            body={
              recvRows.length ? (
                <DataTable headers={["Address", "Block height", "Amount (TLS)"]} rows={recvRows} />
              ) : (
                <p className="muted">No receives of 1,000 TLS or more in this window yet.</p>
              )
            }
          />
        </div>
        <MyCard
          header="Movements list"
          body={
            listRows.length ? (
              <>
                <div className="move-list">
                  <DataTable
                    headers={["Timestamp [UTC]", "Address", "Transaction hash", "Confirmations", "Amount"]}
                    rows={listRows}
                  />
                </div>
                <div className="pager">
                  <button className="btn" disabled={pageSafe <= 0} onClick={() => setPage(pageSafe - 1)}>
                    Previous
                  </button>
                  <span className="muted">
                    {pageSafe * pageSize + 1}–{Math.min(list.length, pageSafe * pageSize + pageSize)} of {fmt(list.length, 0)}
                  </span>
                  <button className="btn" disabled={pageSafe >= pages - 1} onClick={() => setPage(pageSafe + 1)}>
                    Next
                  </button>
                </div>
              </>
            ) : (
              <p className="muted">{data.scanning ? "Scanning recent blocks…" : "No movements in this window yet."}</p>
            )
          }
        />
      </div>
    </Page>
  );
}

export function BurnsPage() {
  const { data, error } = useApi<any>("/api/insights/burns");
  if (!data) return <Page kicker="Supply" title="Burned Coins"><Loading error={error} /></Page>;
  return (
    <Page
      kicker="Supply"
      title="Burned Coins"
      lead="TLS sent to protocol burn addresses when assets are issued or tags are applied. Totals are live balances on this node."
    >
      <div className="gold-inline">
        <span>Total received by burn addresses</span>
        <strong>{fmt(data.total, 2)} TLS</strong>
      </div>
      <div className="card">
        <DataTable
          headers={["Purpose", "Fee", "Address", "Received"]}
          rows={(data.items || []).map((row: any) => [
            row.purpose,
            row.fee == null ? "—" : row.fee + " TLS",
            addrLink(row.address),
            fmt(row.received, 4) + " TLS",
          ])}
        />
      </div>
    </Page>
  );
}

function ShareBars({
  rows,
  nameOf,
}: {
  rows: { share?: number; hashrate?: number; blocks?: number }[];
  nameOf: (row: any) => React.ReactNode;
}) {
  if (!rows.length) return <p className="muted">Scanning recent blocks…</p>;
  return (
    <>
      <div className="dist-bars hash-bars">
        {rows.map((row: any, i: number) => (
          <div key={i} className="dist-row">
            <div className="dist-label">{nameOf(row)}</div>
            <div className="dist-track">
              <div className="dist-fill" style={{ width: Math.min(100, row.share || 0) + "%" }} />
            </div>
            <div className="dist-meta">
              {fmt(row.share, 1)}% · {fmtHashrate(row.hashrate)} · {fmt(row.blocks, 0)} blk
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

const VERSION_COLORS: Record<string, string> = {
  "Telestai 2.1.4": "#7dbe8a",
  "Telestai 2.1.7": "#6ea8d8",
  "Telestai 2.1.8": "#bca55d",
  "Telestai 2.1.9": "#c47c5c",
  "Telestai 3.0.0": "#d4c07a",
};

function colorForVersion(label?: string) {
  if (label && VERSION_COLORS[label]) return VERSION_COLORS[label];
  if (label && /3\.0/.test(label)) return "#d4c07a";
  if (label && /2\.1\.9/.test(label)) return "#c47c5c";
  if (label && /2\.1\.8/.test(label)) return "#bca55d";
  if (label && /2\.1\.7/.test(label)) return "#6ea8d8";
  if (label && /2\.1\.4/.test(label)) return "#7dbe8a";
  return "#888888";
}

function NodeMap({ peers }: { peers: any[] }) {
  const w = 900;
  const h = 420;
  const [hover, setHover] = React.useState<any>(null);
  const plotted = (peers || []).filter((p) => p.lat != null && p.lon != null);
  const lan = (peers || []).filter((p) => p.private);
  return (
    <div className="node-map-wrap">
      <svg viewBox={`0 0 ${w} ${h}`} className="node-map" role="img" aria-label="Peer node map">
        <rect width={w} height={h} fill="#0b1018" />
        {[...Array(12)].map((_, i) => (
          <line
            key={"v" + i}
            x1={(i * w) / 12}
            y1={0}
            x2={(i * w) / 12}
            y2={h}
            stroke="#1a2230"
            strokeWidth="1"
          />
        ))}
        {[...Array(6)].map((_, i) => (
          <line
            key={"h" + i}
            x1={0}
            y1={(i * h) / 6}
            x2={w}
            y2={(i * h) / 6}
            stroke="#1a2230"
            strokeWidth="1"
          />
        ))}
        <text x={16} y={24} fill="#5a6578" fontSize="11">
          90°N
        </text>
        <text x={16} y={h - 12} fill="#5a6578" fontSize="11">
          90°S
        </text>
        {plotted.map((p, i) => {
          const x = ((Number(p.lon) + 180) / 360) * w;
          const y = ((90 - Number(p.lat)) / 180) * h;
          return (
            <circle
              key={p.addr + i}
              cx={x}
              cy={y}
              r={hover === p ? 8 : 6}
              fill={colorForVersion(p.versionLabel)}
              stroke="#070709"
              strokeWidth="1.5"
              onMouseEnter={() => setHover(p)}
              onMouseLeave={() => setHover(null)}
            />
          );
        })}
      </svg>
      <div className="node-map-legend">
        {(peers || [])
          .reduce((acc: string[], p: any) => {
            const v = p.versionLabel || "unknown";
            if (!acc.includes(v)) acc.push(v);
            return acc;
          }, [])
          .map((v: string) => (
            <span key={v} className="map-chip">
              <i style={{ background: colorForVersion(v) }} />
              {v}
            </span>
          ))}
        {lan.length ? (
          <span className="map-chip">
            <i style={{ background: "#888" }} />
            {lan.length} LAN (not plotted)
          </span>
        ) : null}
      </div>
      {hover ? (
        <div className="node-map-pop">
          <div className="mono">{hover.addr}</div>
          <div>
            {hover.versionLabel}
            {hover.city || hover.country ? " · " + [hover.city, hover.country].filter(Boolean).join(", ") : ""}
          </div>
        </div>
      ) : plotted.length === 0 ? (
        <p className="muted">No public peers with coordinates yet. LAN peers stay off the map.</p>
      ) : null}
    </div>
  );
}

export function MiningPage() {
  const [data, setData] = React.useState<any>(null);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    let n = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const load = () => {
      axios
        .get("/api/insights/mining")
        .then((r) => {
          setData(r.data);
          if ((r.data.scanning || !(r.data.miners || []).length) && n < 30) {
            n += 1;
            timer = setTimeout(load, 4000);
          }
        })
        .catch((e) => setError(e.message || "Failed to load"));
    };
    load();
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, []);

  if (!data) return <Page kicker="Work" title="Mining"><Loading error={error} /></Page>;
  const windowLabel = data.window?.blocks
    ? "Last ~" +
      (data.window.hours || 24) +
      "h · blocks " +
      fmt(data.window.from, 0) +
      "–" +
      fmt(data.window.to, 0)
    : "Scanning coinbases…";
  return (
    <Page
      kicker="Work"
      title="Mining"
      lead="Meraki proof of work. Hashrate by version uses the coinbase string; pools usually tag the pool name, not Core 2.1.x / 3.0.0."
    >
      <div className="stat-grid">
        <div className="stat-cell">
          <div className="label">Difficulty</div>
          <div className="value">{fmt(data.difficulty, 4)}</div>
        </div>
        <div className="stat-cell">
          <div className="label">Hashrate</div>
          <div className="value">{fmtHashrate(data.networkhashps)}</div>
        </div>
        <div className="stat-cell">
          <div className="label">Block reward</div>
          <div className="value">{fmt(data.subsidy, 0)} TLS</div>
        </div>
        <div className="stat-cell">
          <div className="label">Blocks to halving</div>
          <div className="value">{fmt(data.nextHalving, 0)}</div>
        </div>
      </div>
      <p className="muted">{windowLabel}</p>
      {data.note ? <div className="banner">{data.note}</div> : null}
      <MyCard
        header="Hashrate by Core version"
        body={
          <ShareBars
            rows={data.versions || []}
            nameOf={(row) => <span>{row.version}</span>}
          />
        }
      />
      <MyCard
        header="Hashrate by pool tag"
        body={
          <ShareBars
            rows={data.pools || []}
            nameOf={(row) => <span className="mono">{row.tag}</span>}
          />
        }
      />
      <MyCard
        header="Hashrate by payout address"
        body={
          (data.miners || []).length ? (
            <DataTable
              headers={["Miner", "Blocks", "Share", "Est. hashrate"]}
              rows={data.miners.map((m: any) => [
                m.address && m.address !== "unknown" ? addrLink(m.address) : "unknown",
                m.blocks,
                fmt(m.share, 1) + "%",
                fmtHashrate(m.hashrate),
              ])}
            />
          ) : (
            <p className="muted">Scanning recent blocks for coinbase payouts…</p>
          )
        }
      />
    </Page>
  );
}

export function NetworkPage() {
  const { data, error } = useApi<any>("/api/insights/network");
  if (!data) return <Page kicker="Peers" title="Network"><Loading error={error} /></Page>;
  return (
    <Page
      kicker="Peers"
      title="Network"
      lead="What this explorer node currently sees. Peer versions are connected wallets/daemons, not mining hashrate."
    >
      <div className="stat-grid">
        <div className="stat-cell">
          <div className="label">User agent</div>
          <div className="value small">{data.subversion || "—"}</div>
        </div>
        <div className="stat-cell">
          <div className="label">Connections</div>
          <div className="value">{data.connections ?? 0}</div>
        </div>
        <div className="stat-cell">
          <div className="label">Inbound</div>
          <div className="value">{data.connections_in ?? "—"}</div>
        </div>
        <div className="stat-cell">
          <div className="label">Outbound</div>
          <div className="value">{data.connections_out ?? "—"}</div>
        </div>
      </div>
      {data.note ? <div className="banner">{data.note}</div> : null}
      <MyCard header="Node map" body={<NodeMap peers={data.peers || []} />} />
      <MyCard
        header="Connected versions"
        body={
          <DataTable
            headers={["Version", "Peers"]}
            rows={(data.versions || []).map((v: any) => [v.subver, v.count])}
          />
        }
      />
      <MyCard
        header="Peers"
        body={
          <DataTable
            headers={["Address", "Location", "Version", "Ping", "Synced"]}
            rows={(data.peers || []).map((p: any) => [
              <span className="mono">{p.addr}</span>,
              p.private
                ? "LAN"
                : [p.city, p.country].filter(Boolean).join(", ") || "—",
              p.versionLabel || p.subver,
              p.pingtime != null ? (p.pingtime * 1000).toFixed(0) + " ms" : "—",
              p.synced_blocks != null ? fmt(p.synced_blocks, 0) : "—",
            ])}
          />
        }
      />
    </Page>
  );
}

export function MarketsPage() {
  const { data, error } = useApi<any>("/api/insights/markets");
  if (!data) return <Page kicker="Price" title="Markets"><Loading error={error} /></Page>;
  return (
    <Page kicker="Price" title="Markets" lead="Public market data when a TLS feed exists. Empty means none is indexed yet.">
      {data.note ? <div className="banner">{data.note}</div> : null}
      <div className="card">
        <DataTable
          headers={["Source", "Pair", "Price", "24h", "Volume"]}
          rows={
            (data.items || []).length
              ? data.items.map((m: any) => [
                  m.exchange,
                  (m.symbol || "tls").toUpperCase() + "/USD",
                  m.price != null ? "$" + fmt(m.price, 6) : "—",
                  m.change24h != null ? fmt(m.change24h, 2) + "%" : "—",
                  m.volume != null ? "$" + fmt(m.volume, 0) : "—",
                ])
              : [["—", "TLS/USD", "—", "—", "—"]]
          }
        />
      </div>
    </Page>
  );
}

export function StatsPage() {
  const { data, error } = useApi<any>("/api/insights/stats");
  if (!data) return <Page kicker="Overview" title="Chain Statistics"><Loading error={error} /></Page>;
  return (
    <Page kicker="Overview" title="Chain Statistics" lead="Live figures from this node, plus the Meraki emission schedule.">
      <div className="stat-grid">
        <div className="stat-cell">
          <div className="label">Height</div>
          <div className="value">{fmt(data.height, 0)}</div>
        </div>
        <div className="stat-cell">
          <div className="label">Supply</div>
          <div className="value">{fmt(data.supply, 0)}</div>
        </div>
        <div className="stat-cell">
          <div className="label">UTXOs</div>
          <div className="value">{fmt(data.txouts, 0)}</div>
        </div>
        <div className="stat-cell">
          <div className="label">Transactions</div>
          <div className="value">{fmt(data.transactions, 0)}</div>
        </div>
        <div className="stat-cell">
          <div className="label">Difficulty</div>
          <div className="value">{fmt(data.difficulty, 3)}</div>
        </div>
        <div className="stat-cell">
          <div className="label">Hashrate</div>
          <div className="value">{fmtHashrate(data.networkhashps)}</div>
        </div>
        <div className="stat-cell">
          <div className="label">Block time</div>
          <div className="value">{fmt(data.avgBlockTime, 1)}s</div>
        </div>
        <div className="stat-cell">
          <div className="label">Subsidy</div>
          <div className="value">{fmt(data.subsidy, 0)} TLS</div>
        </div>
        <div className="stat-cell">
          <div className="label">Next halving</div>
          <div className="value">{fmt(data.nextHalvingHeight, 0)}</div>
        </div>
        <div className="stat-cell">
          <div className="label">Assets</div>
          <div className="value">{fmt(data.assets?.total, 0)}</div>
        </div>
      </div>
      <MyCard
        header="Emission"
        body={
          <DataTable
            headers={["Height", "Subsidy", "Theoretical supply"]}
            rows={(data.emission || []).map((e: any) => [
              fmt(e.height, 0),
              fmt(e.subsidy, 2) + " TLS",
              fmt(e.supply, 0) + " TLS",
            ])}
          />
        }
      />
    </Page>
  );
}
